import type { ApiClient } from "./shared";

export interface SubscriptionSelfServiceUrls {
	readonly cancel_url: string | null;
	readonly open_mode?: "iframe" | "tab";
	readonly update_url: string | null;
}

export interface PaddleStatusResponse {
	readonly cancel_url: string | null;
	readonly open_mode?: "iframe" | "tab";
	readonly overview_url?: string | null;
	readonly quantity?: number;
	readonly status?: string;
	readonly update_url: string | null;
}

export interface WorkspaceRenewalResponse {
	readonly renewing: boolean;
}

export interface PaddlePaymentHistoryItem {
	readonly amount: number;
	readonly currency: string;
	readonly id: number | string;
	readonly is_one_off_charge: boolean;
	readonly is_paid: number;
	readonly payout_date: string;
	readonly receipt_url: string;
	readonly subscription_id: number | string;
}

export type BillingPlanInterval = "month" | "year";

export const createBillingStore = (apiClient: ApiClient) => {
	const getSelfServiceUrl = async (workspaceId: string): Promise<SubscriptionSelfServiceUrls> =>
		apiClient.get<SubscriptionSelfServiceUrls>("/billing/self-service", {
			workspaceId,
		});

	/** Paddle Classic self-serve URLs + status from Payment_Subscriptions. */
	const getPaddleStatus = async (workspaceId: string): Promise<PaddleStatusResponse> =>
		apiClient.get<PaddleStatusResponse>("/billing/status", undefined, {
			"workspace-id": workspaceId,
		});

	/** Live check: true while the subscription is still scheduled to renew. */
	const getRenewalStatus = async (workspaceId: string): Promise<WorkspaceRenewalResponse> =>
		apiClient.get<WorkspaceRenewalResponse>("/billing/renewal", undefined, {
			"workspace-id": workspaceId,
		});

	const updateQuantity = async (workspaceId: string, quantity: number): Promise<unknown> =>
		apiClient.get<unknown>("/billing/quantity", {
			workspaceId,
			quantity,
		});

	const changePlan = async (workspaceId: string, plan: BillingPlanInterval): Promise<unknown> =>
		apiClient.post<unknown>("/billing/plan", {
			workspaceId,
			plan,
		});

	const getPaymentHistory = async (
		workspaceId: string,
		from: string,
		to: string
	): Promise<readonly PaddlePaymentHistoryItem[]> => {
		const raw = await apiClient.get<unknown>("/billing/payments", {
			workspaceId,
			from,
			to,
		});
		if (Array.isArray(raw)) {
			return raw as PaddlePaymentHistoryItem[];
		}
		if (raw && typeof raw === "object" && "payments" in raw && Array.isArray((raw as { payments: unknown }).payments)) {
			return (raw as { payments: PaddlePaymentHistoryItem[] }).payments;
		}
		return [];
	};

	return {
		actions: {
			getSelfServiceUrl,
			getPaddleStatus,
			getRenewalStatus,
			updateQuantity,
			changePlan,
			getPaymentHistory,
		},
	};
};

export type BillingStore = ReturnType<typeof createBillingStore>;
